@file:NoLiveLiterals

package com.example.borshi.web.nutritionist

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.firebase.FirebaseService
import com.example.borshi.web.nutritionist.models.*
import com.example.borshi.web.nutritionist.repository.FirebaseNutritionistRepository
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import com.example.borshi.web.nutritionist.ui.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens — identical to the rest of the app (HomeScreen / ProteinPlans)
// ─────────────────────────────────────────────────────────────────────────────
private object DS {
    const val ink        = "#0D2B2E"
    const val inkMid     = "#3D5A5E"
    const val inkLight   = "#7A9598"
    const val teal       = "#26A69A"
    const val tealDeep   = "#1A7A72"
    const val tealGhost  = "#E0F2F1"
    const val tealFaint  = "#F0FAFA"
    const val crimson    = "#D11243"
    const val surface    = "#F5F9F8"
    const val white      = "#FFFFFF"
    const val border     = "#E0EDEB"
    const val borderDark = "#C4DBD8"
    const val success    = "#2E7D32"
    const val successBg  = "#E8F5E9"
    const val warn       = "#E65100"
    const val warnBg     = "#FFF3E0"
    const val danger     = "#C62828"
    const val dangerBg   = "#FCE4EC"

    val sp4  = 4.px;  val sp8  = 8.px;  val sp12 = 12.px; val sp16 = 16.px
    val sp20 = 20.px; val sp24 = 24.px; val sp32 = 32.px; val sp40 = 40.px
    val sp48 = 48.px; val sp64 = 64.px

    val r8   = 8.px;  val r12 = 12.px; val r16 = 16.px
    val r20  = 20.px; val r24 = 24.px; val rFull = 999.px

    const val shadowSm   = "0 1px 4px rgba(13,43,46,0.06)"
    const val shadowMd   = "0 4px 16px rgba(13,43,46,0.10)"
    const val shadowLg   = "0 8px 32px rgba(13,43,46,0.14)"
    const val shadowTeal = "0 4px 16px rgba(38,166,154,0.25)"
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared category helpers — single source of truth (not duplicated per-screen)
// ─────────────────────────────────────────────────────────────────────────────
internal fun categoryEmoji(name: String) = when (name) {
    "MUSCLE_BUILDING" -> "💪"; "WEIGHT_LOSS"    -> "🔥"; "HEART_HEALTH"  -> "❤️"
    "ATHLETE"         -> "🏃"; "DIABETES"       -> "💉"; "PREGNANCY"     -> "🤰"
    "SENIOR"          -> "👴"; "PCOS"           -> "🌸"; "GENERAL_HEALTH"-> "🌿"
    else              -> "📋"
}

internal fun categoryLabel(name: String) = when (name) {
    "MUSCLE_BUILDING" -> "Muscle Building"; "WEIGHT_LOSS"    -> "Weight Loss"
    "HEART_HEALTH"    -> "Heart Health";    "ATHLETE"        -> "Athlete"
    "DIABETES"        -> "Diabetes";        "PREGNANCY"      -> "Pregnancy"
    "SENIOR"          -> "Senior";          "PCOS"           -> "PCOS"
    "GENERAL_HEALTH"  -> "General Health";  else             -> name
}

// Returns Pair(bgColor, fgColor) for category pills — matches ProteinPlansScreen
internal fun categoryStyle(name: String): Pair<String, String> = when (name) {
    "MUSCLE_BUILDING" -> "#FFF3E0" to DS.warn
    "WEIGHT_LOSS"     -> DS.dangerBg to DS.danger
    "HEART_HEALTH"    -> "#FFEBEE" to "#B71C1C"
    "ATHLETE"         -> DS.tealGhost to DS.tealDeep
    "DIABETES"        -> "#FFF8E1" to "#F57F17"
    "PREGNANCY"       -> DS.dangerBg to "#880E4F"
    "SENIOR"          -> "#E8EAF6" to "#283593"
    "PCOS"            -> "#F3E5F5" to "#6A1B9A"
    else              -> DS.successBg to DS.success
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS injected once for the whole nutritionist module
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun InjectDashboardStyles() {
    DisposableEffect(Unit) {
        val style = kotlinx.browser.document.createElement("style")
        style.textContent = """
            @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'DM Sans', sans-serif; background: ${DS.surface}; color: ${DS.ink}; -webkit-font-smoothing: antialiased; }

            @keyframes fadeUp   { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
            @keyframes shimmer  { 0% { background-position:-600px 0; } 100% { background-position:600px 0; } }
            @keyframes scaleIn  { from { opacity:0; transform:scale(0.97); } to { opacity:1; transform:scale(1); } }
            @keyframes slideIn  { from { opacity:0; transform:translateX(-12px); } to { opacity:1; transform:translateX(0); } }

            .fade-up  { animation: fadeUp  0.45s cubic-bezier(0.22,1,0.36,1) both; }
            .scale-in { animation: scaleIn 0.35s cubic-bezier(0.22,1,0.36,1) both; }
            .delay-1  { animation-delay:0.05s; } .delay-2 { animation-delay:0.10s; }
            .delay-3  { animation-delay:0.15s; } .delay-4 { animation-delay:0.20s; }

            .skeleton {
                background: linear-gradient(90deg,#E8F2F1 25%,#F4FAFA 50%,#E8F2F1 75%);
                background-size: 600px 100%;
                animation: shimmer 1.4s ease-in-out infinite;
                border-radius: 10px;
            }

            /* ── Nav ── */
            .dash-nav {
                position: sticky; top: 0; z-index: 50;
                background: rgba(245,249,248,0.92);
                backdrop-filter: blur(14px);
                border-bottom: 1px solid ${DS.border};
            }
            .nav-tabs { display:flex; gap:4px; background:${DS.white}; border:1.5px solid ${DS.border}; border-radius:999px; padding:4px; }
            .nav-tab  { padding:8px 18px; border-radius:999px; font-size:13px; font-weight:600; cursor:pointer; border:none; background:transparent; color:${DS.inkMid}; font-family:'DM Sans',sans-serif; transition:all 0.15s ease; white-space:nowrap; }
            .nav-tab.active { background:${DS.ink}; color:white; }

            /* ── Cards ── */
            .card {
                background: ${DS.white}; border-radius: 20px;
                border: 1.5px solid ${DS.border};
                box-shadow: ${DS.shadowSm};
                transition: transform 0.22s cubic-bezier(0.22,1,0.36,1), box-shadow 0.22s ease, border-color 0.22s ease;
            }
            .card:hover { transform: translateY(-3px); box-shadow: ${DS.shadowMd}; border-color: ${DS.borderDark}; }
            .card-clickable:hover { transform: translateY(-4px); box-shadow: ${DS.shadowLg}; border-color: ${DS.teal}; cursor: pointer; }

            /* ── Stat cards ── */
            .stat-card {
                background: ${DS.white}; border-radius: 18px;
                border: 1.5px solid ${DS.border}; padding: 24px;
                transition: transform 0.2s ease, box-shadow 0.2s ease;
            }
            .stat-card:hover { transform: translateY(-3px); box-shadow: ${DS.shadowMd}; }

            /* ── Buttons ── */
            .btn-primary {
                display:inline-flex; align-items:center; gap:8px;
                padding:10px 22px; border-radius:999px; font-size:13px;
                font-weight:700; cursor:pointer; border:none;
                background:${DS.ink}; color:white;
                font-family:'DM Sans',sans-serif; transition:all 0.2s ease;
                box-shadow:${DS.shadowSm};
            }
            .btn-primary:hover { background:#1A3D40; transform:translateY(-1px); }

            .btn-teal {
                display:inline-flex; align-items:center; gap:8px;
                padding:10px 22px; border-radius:999px; font-size:13px;
                font-weight:700; cursor:pointer; border:none;
                background:${DS.teal}; color:white;
                font-family:'DM Sans',sans-serif; transition:all 0.2s ease;
                box-shadow:${DS.shadowTeal};
            }
            .btn-teal:hover { background:${DS.tealDeep}; transform:translateY(-1px); }

            .btn-ghost {
                display:inline-flex; align-items:center; gap:6px;
                padding:8px 18px; border-radius:999px; font-size:13px;
                font-weight:600; cursor:pointer;
                border:1.5px solid ${DS.border};
                background:transparent; color:${DS.inkMid};
                font-family:'DM Sans',sans-serif; transition:all 0.15s ease;
            }
            .btn-ghost:hover { border-color:${DS.teal}; color:${DS.teal}; }

            .btn-danger {
                display:inline-flex; align-items:center; gap:6px;
                padding:8px 18px; border-radius:999px; font-size:13px;
                font-weight:600; cursor:pointer;
                border:1.5px solid ${DS.dangerBg}; background:${DS.dangerBg}; color:${DS.danger};
                font-family:'DM Sans',sans-serif; transition:all 0.15s ease;
            }
            .btn-danger:hover { background:${DS.danger}; color:white; border-color:${DS.danger}; }

            .btn-success {
                display:inline-flex; align-items:center; gap:6px;
                padding:8px 18px; border-radius:999px; font-size:13px;
                font-weight:600; cursor:pointer;
                border:1.5px solid ${DS.successBg}; background:${DS.successBg}; color:${DS.success};
                font-family:'DM Sans',sans-serif; transition:all 0.15s ease;
            }
            .btn-success:hover { background:${DS.success}; color:white; border-color:${DS.success}; }

            /* ── Filter chip ── */
            .filter-chip { padding:7px 16px; border-radius:999px; font-size:12px; font-weight:600; cursor:pointer; border:1.5px solid ${DS.border}; background:${DS.white}; color:${DS.inkMid}; font-family:'DM Sans',sans-serif; transition:all 0.15s ease; white-space:nowrap; }
            .filter-chip:hover { border-color:${DS.teal}; color:${DS.teal}; background:${DS.tealFaint}; }
            .filter-chip.active { background:${DS.teal}; border-color:${DS.teal}; color:white; box-shadow:${DS.shadowTeal}; }

            /* ── Category pill ── */
            .cat-pill { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border-radius:999px; font-size:11px; font-weight:700; }

            /* ── Status badge ── */
            .badge-active   { background:${DS.successBg}; color:${DS.success}; }
            .badge-inactive { background:${DS.dangerBg};  color:${DS.danger}; }
            .badge-paused   { background:${DS.warnBg};    color:${DS.warn}; }

            /* ── Search input ── */
            .search-wrap { position:relative; }
            .search-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); pointer-events:none; font-size:14px; color:${DS.inkLight}; }
            .search-input {
                width:100%; padding:11px 40px 11px 40px;
                border-radius:${DS.r12}px; border:1.5px solid ${DS.border};
                font-size:14px; outline:none; color:${DS.ink};
                background:${DS.white}; font-family:'DM Sans',sans-serif;
                transition:border-color 0.15s ease;
            }
            .search-input:focus { border-color:${DS.teal}; }

            /* ── Select ── */
            .ds-select {
                padding:10px 14px; border:1.5px solid ${DS.border}; border-radius:${DS.r12}px;
                font-size:13px; font-family:'DM Sans',sans-serif; color:${DS.ink};
                background:${DS.white}; cursor:pointer; outline:none;
                transition:border-color 0.15s ease;
            }
            .ds-select:focus { border-color:${DS.teal}; }

            /* ── Adherence bar ── */
            .adh-track { background:${DS.border}; border-radius:999px; height:6px; overflow:hidden; }
            .adh-fill  { height:100%; border-radius:999px; transition:width 0.4s ease; }

            /* ── Section title ── */
            .section-label { font-family:'Syne',sans-serif; font-size:11px; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:${DS.teal}; }
            .section-title { font-family:'Syne',sans-serif; font-size:24px; font-weight:800; color:${DS.ink}; letter-spacing:-0.03em; line-height:1.15; }

            /* ── Divider ── */
            .divider { height:1px; background:linear-gradient(90deg,transparent,${DS.borderDark},transparent); margin:40px 0; }

            /* ── Empty state ── */
            .empty-state { text-align:center; padding:64px 24px; }

            /* ── Info grid (profile / detail) ── */
            .info-grid { display:grid; gap:1px; background:${DS.border}; border-radius:16px; overflow:hidden; border:1px solid ${DS.border}; }
            .info-cell { background:${DS.white}; padding:16px 20px; }

            /* ── Responsive ── */
            @media (max-width:768px) {
                .nav-tabs { display:none; }
                .stat-grid { grid-template-columns: repeat(2,1fr) !important; }
            }
        """.trimIndent()
        kotlinx.browser.document.head?.appendChild(style)
        onDispose { style.remove() }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// NutritionistDashboard — public entry point (replaces the empty facade)
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun NutritionistAppEntryPoint() {
    InjectDashboardStyles()
    NutritionistApp() // Calls the internal coordinator
}

// ─────────────────────────────────────────────────────────────────────────────
// NutritionistApp — coordinator; all state lives here
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun NutritionistApp() {
    var currentView        by remember { mutableStateOf(NutritionistView.DASHBOARD) }
    var selectedPatient    by remember { mutableStateOf<Patient?>(null) }
    var isLoading          by remember { mutableStateOf(true) }
    var nutritionistProfile by remember { mutableStateOf<NutritionistProfile?>(null) }
    var dashboardStats     by remember { mutableStateOf<DashboardStats?>(null) }
    var errorMessage       by remember { mutableStateOf<String?>(null) }

    var showAddUserModal    by remember { mutableStateOf(false) }
    var showCreatePlanModal by remember { mutableStateOf(false) }
    var showAssignPlanModal by remember { mutableStateOf(false) }
    var assignPlanPatientId by remember { mutableStateOf<String?>(null) }
    var patientListRefresh  by remember { mutableStateOf(0) }

    val repository: NutritionistRepository =
        remember { FirebaseNutritionistRepository(FirebaseService) }
    val scope = rememberCoroutineScope()

    var nutritionistId by remember {
        mutableStateOf(FirebaseService.getCurrentUserId() ?: "")
    }

    DisposableEffect(Unit) {
        FirebaseService.init()
        val unsub = FirebaseService.onAuthStateChanged { user ->
            val uid = user?.uid as? String
            if (!uid.isNullOrBlank() && uid != nutritionistId) nutritionistId = uid
        }
        onDispose { unsub() }
    }

    // Stable refresh lambda — wrapped in remember so it isn't recreated each recomposition
    val refreshStats: () -> Unit = remember(nutritionistId) {
        {
            scope.launch {
                repository.getDashboardStats(nutritionistId)
                    .onSuccess { dashboardStats = it }
                    .onFailure { console.error("Stats refresh failed:", it) }
            }
        }
    }

    LaunchedEffect(nutritionistId) {
        if (nutritionistId.isBlank()) return@LaunchedEffect
        isLoading = true; errorMessage = null

        repository.getNutritionistProfile(nutritionistId)
            .onSuccess { nutritionistProfile = it }
            .onFailure {
                nutritionistProfile = NutritionistProfile(
                    nutritionistId  = nutritionistId,
                    name            = "Demo Nutritionist",
                    credentials     = "RD, CDE",
                    specializations = listOf("Weight Management", "Sports Nutrition"),
                    experience      = 5,
                    rating          = 4.8,
                    totalClients    = 0,
                    activeClients   = 0,
                    bio             = "Professional nutritionist",
                    profileImage    = "",
                    contactEmail    = "nutritionist@borshi.com",
                    phone           = "+880-1234-567890",
                    isActive        = true,
                    createdAt       = 0,
                    updatedAt       = 0
                )
            }

        repository.getDashboardStats(nutritionistId)
            .onSuccess { dashboardStats = it }
            .onFailure {
                dashboardStats = DashboardStats(
                    totalPatients     = 0, activePatients   = 0,
                    averageAdherence  = 0, plansCreated     = 0,
                    activeAlerts      = 0, todayAppointments = 0,
                    pendingReviews    = 0
                )
            }

        isLoading = false
    }

    // Resolve the nutritionistId for child components
    val resolvedId = nutritionistProfile?.nutritionistId ?: nutritionistId

    Div({ style { width(100.percent); minHeight(100.vh); backgroundColor(Color(DS.surface)) }}) {

        // ── Sticky nav ────────────────────────────────────────────────────
        DashboardNav(
            profile      = nutritionistProfile,
            currentView  = currentView,
            // FIX: BIOMARKERS tab only enabled when a patient is selected
            onViewChange = { view ->
                if (view == NutritionistView.BIOMARKERS && selectedPatient == null) {
                    currentView = NutritionistView.PATIENTS
                } else {
                    currentView = view
                    // Clear patient selection when navigating away from detail
                    if (view != NutritionistView.PATIENTS && view != NutritionistView.BIOMARKERS) {
                        selectedPatient = null
                    }
                }
            }
        )

        // ── Main content ──────────────────────────────────────────────────
        Div({ style {
            maxWidth(1400.px); property("margin","0 auto"); padding(DS.sp32, DS.sp24, DS.sp64)
        }}) {
            when {
                errorMessage != null -> DashboardError(errorMessage!!) {
                    errorMessage = null; isLoading = true
                    scope.launch {
                        repository.getDashboardStats(nutritionistId)
                            .onSuccess { dashboardStats = it; isLoading = false }
                            .onFailure { errorMessage = it.message; isLoading = false }
                    }
                }

                isLoading -> DashboardSkeleton()

                selectedPatient != null -> {
                    PatientDetailView(
                        patient         = selectedPatient!!,
                        repository      = repository,
                        onBack          = { selectedPatient = null },
                        onPatientUpdated = { patientListRefresh++; refreshStats() }
                    )
                }

                else -> when (currentView) {
                    NutritionistView.DASHBOARD -> DashboardOverview(
                        stats          = dashboardStats,
                        onAddUser      = { showAddUserModal = true },
                        onCreatePlan   = { showCreatePlanModal = true },
                        onAssignPlan   = { showAssignPlanModal = true },
                        onBrowseCatalog = { currentView = NutritionistView.FOOD_CATALOG }
                    )
                    NutritionistView.PATIENTS -> PatientsListView(
                        repository      = repository,
                        refreshTrigger  = patientListRefresh,
                        nutritionistId  = resolvedId,
                        onPatientClick  = { patient ->
                            selectedPatient = patient
                        }
                    )
                    NutritionistView.PLANS -> PlansListView(
                        repository     = repository,
                        nutritionistId = resolvedId
                    )
                    NutritionistView.FOOD_CATALOG -> FoodCatalogBrowserView(repository)
                    NutritionistView.MESSAGING -> MessagingView(
                        repository     = repository,
                        nutritionistId = resolvedId
                    )
                    // FIX: BIOMARKERS without a patient can't reach here (nav guards above)
                    // but guard defensively anyway
                    NutritionistView.BIOMARKERS -> {
                        if (selectedPatient != null) {
                            BiomarkerTrackingView(
                                patientId   = selectedPatient!!.patientId,
                                patientName = selectedPatient!!.name,
                                onBack      = {
                                    selectedPatient = null
                                    currentView     = NutritionistView.PATIENTS
                                }
                            )
                        } else {
                            // Should not normally be reached — redirect
                            LaunchedEffect(Unit) { currentView = NutritionistView.PATIENTS }
                        }
                    }
                    NutritionistView.PROFILE -> ProfileView(nutritionistProfile)
                }
            }
        }

        // ── Modals ────────────────────────────────────────────────────────
        AddUserModal(
            isOpen         = showAddUserModal,
            repository     = repository,
            nutritionistId = resolvedId,
            onClose        = { showAddUserModal = false },
            onUserAdded    = {
                patientListRefresh++; refreshStats()
                showAddUserModal = false
                currentView = NutritionistView.PATIENTS
            }
        )
        CreatePlanModal(
            isOpen           = showCreatePlanModal,
            repository       = repository,
            nutritionistId   = resolvedId,
            nutritionistName = nutritionistProfile?.name ?: "Nutritionist",
            onClose          = { showCreatePlanModal = false },
            onPlanCreated    = { refreshStats(); showCreatePlanModal = false; currentView = NutritionistView.PLANS }
        )
        AssignPlanModal(
            isOpen              = showAssignPlanModal,
            repository          = repository,
            nutritionistId      = resolvedId,
            preSelectedPatientId = assignPlanPatientId,
            onClose             = { showAssignPlanModal = false; assignPlanPatientId = null },
            onPlanAssigned      = {
                showAssignPlanModal = false; assignPlanPatientId = null
                refreshStats(); patientListRefresh++
            }
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard nav
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DashboardNav(
    profile: NutritionistProfile?,
    currentView: NutritionistView,
    onViewChange: (NutritionistView) -> Unit
) {
    Div({ classes("dash-nav") }) {
        Div({ style {
            maxWidth(1400.px); property("margin","0 auto")
            padding(0.px, DS.sp24); height(68.px)
            display(DisplayStyle.Flex); alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
        }}) {
            // Wordmark
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(10.px) }}) {
                Span({ style { fontSize(22.px) }}) { Text("🐙") }
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(17.px); fontWeight("800")
                    color(Color(DS.ink)); property("letter-spacing","-0.03em")
                }}) { Text("Borshi Clinic") }
                if (profile != null) {
                    Span({ style {
                        fontSize(11.px); fontWeight("600"); color(Color(DS.inkLight))
                        marginLeft(DS.sp8); paddingLeft(DS.sp8)
                        property("border-left","1px solid ${DS.border}")
                    }}) { Text(profile.credentials) }
                }
            }

            // Nav tabs
            Div({ classes("nav-tabs") }) {
                listOf(
                    NutritionistView.DASHBOARD    to "Dashboard",
                    NutritionistView.PATIENTS     to "Patients",
                    NutritionistView.PLANS        to "Plans",
                    NutritionistView.FOOD_CATALOG to "Catalog",
                    NutritionistView.MESSAGING    to "Messages",
                    NutritionistView.PROFILE      to "Profile"
                ).forEach { (view, label) ->
                    Button({
                        attr("class", if (currentView == view) "nav-tab active" else "nav-tab")
                        onClick { onViewChange(view) }
                    }) { Text(label) }
                }
            }

            // Avatar pill
            if (profile != null) {
                Div({ style {
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp8)
                    backgroundColor(Color(DS.tealGhost)); borderRadius(DS.rFull)
                    padding(DS.sp8, DS.sp16)
                    property("cursor","pointer")
                }
                    onClick { onViewChange(NutritionistView.PROFILE) }
                }) {
                    Div({ style {
                        width(28.px); height(28.px); borderRadius(50.percent)
                        property("background","linear-gradient(135deg,${DS.ink},${DS.teal})")
                        display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                        fontSize(12.px); fontWeight("800"); color(Color(DS.white))
                        flexShrink(0)
                    }}) {
                        Text(profile.name.take(1).uppercase())
                    }
                    Div({ style { fontSize(13.px); fontWeight("600"); color(Color(DS.ink)) }}) {
                        Text(profile.name.split(" ").first())
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Skeleton loader for initial page load
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DashboardSkeleton() {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp24) }}) {
        // Stat row skeleton
        Div({ style {
            display(DisplayStyle.Grid)
            property("grid-template-columns","repeat(auto-fill,minmax(180px,1fr))")
            gap(DS.sp16)
        }}) {
            repeat(5) {
                Div({ classes("skeleton"); style { height(96.px); borderRadius(DS.r16) }}) {}
            }
        }
        // Card skeleton
        Div({ style {
            display(DisplayStyle.Grid)
            property("grid-template-columns","repeat(auto-fill,minmax(340px,1fr))")
            gap(DS.sp16)
        }}) {
            repeat(4) {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(DS.sp12) }}) {
                    Div({ classes("skeleton"); style { height(200.px); borderRadius(DS.r20) }}) {}
                    Div({ classes("skeleton"); style { height(14.px); width(60.percent) }}) {}
                    Div({ classes("skeleton"); style { height(12.px); width(40.percent) }}) {}
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Error state
// ─────────────────────────────────────────────────────────────────────────────
@Composable
private fun DashboardError(message: String, onRetry: () -> Unit) {
    Div({ style { textAlign("center"); padding(DS.sp64, 0.px) }}) {
        Div({ style { fontSize(48.px); marginBottom(DS.sp16) }}) { Text("⚠️") }
        Div({ style {
            fontFamily("Syne, sans-serif"); fontSize(20.px); fontWeight("800")
            color(Color(DS.ink)); marginBottom(DS.sp8)
        }}) { Text("Something went wrong") }
        Div({ style {
            fontSize(14.px); color(Color(DS.inkLight)); marginBottom(DS.sp24); maxWidth(400.px); property("margin","0 auto ${DS.sp24.value}px")
        }}) { Text(message) }
        Button({ classes("btn-teal"); onClick { onRetry() } }) { Text("Try Again") }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Patients List View
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun PatientsListView(
    repository: NutritionistRepository,
    refreshTrigger: Int = 0,
    nutritionistId: String = FirebaseService.getCurrentUserId() ?: "nutritionist_001",
    onPatientClick: (Patient) -> Unit
) {
    var allPatients  by remember { mutableStateOf<List<Patient>>(emptyList()) }
    var searchQuery  by remember { mutableStateOf("") }
    var filterStatus by remember { mutableStateOf("All") }
    var sortBy       by remember { mutableStateOf("recent") }
    var isLoading    by remember { mutableStateOf(true) }
    var error        by remember { mutableStateOf<String?>(null) }
    val scope        = rememberCoroutineScope()

    LaunchedEffect(refreshTrigger) {
        scope.launch {
            isLoading = true; error = null
            repository.getPatients(nutritionistId)
                .onSuccess { allPatients = it; isLoading = false }
                .onFailure { error = it.message; isLoading = false }
        }
    }

    val filtered = remember(allPatients, searchQuery, filterStatus, sortBy) {
        var r = allPatients
        if (searchQuery.isNotBlank()) {
            val q = searchQuery.lowercase()
            r = r.filter { it.name.lowercase().contains(q) || it.email.lowercase().contains(q) || it.phone.contains(q) }
        }
        if (filterStatus != "All") r = r.filter { it.status.name == filterStatus }
        r = when (sortBy) {
            "name"      -> r.sortedBy { it.name }
            "adherence" -> r.sortedByDescending { it.adherenceScore }
            else        -> r.sortedByDescending { it.createdAt }
        }
        r
    }

    Div({ classes("fade-up") }) {
        // Section header
        PageHeader(
            label    = "Patient Management",
            title    = "Patients",
            count    = allPatients.size,
            subtitle = "Search, filter and manage your patient roster"
        )

        // Filters bar
        Div({ style {
            display(DisplayStyle.Flex); gap(DS.sp12); marginBottom(DS.sp24); flexWrap(FlexWrap.Wrap)
            alignItems(AlignItems.Center)
        }}) {
            // Search
            Div({ style { flex(1); minWidth(260.px) }}) {
                DsSearchBar(searchQuery, "Search by name, email or phone…") { searchQuery = it }
            }
            // Status filter
            Select({
                classes("ds-select")
                onChange { filterStatus = it.value ?: "All" }
            }) {
                Option("All")      { Text("All Status") }
                Option("ACTIVE")   { Text("Active") }
                Option("INACTIVE") { Text("Inactive") }
                Option("PAUSED")   { Text("Paused") }
            }
            // Sort
            Select({
                classes("ds-select")
                onChange { sortBy = it.value ?: "recent" }
            }) {
                Option("recent")    { Text("Most Recent") }
                Option("name")      { Text("Name A–Z") }
                Option("adherence") { Text("Adherence") }
            }
        }

        // Results count
        if (!isLoading && searchQuery.isNotBlank()) {
            ResultCount(filtered.size, allPatients.size, "patients")
        }

        when {
            isLoading                -> DashboardSkeleton()
            error != null            -> DashboardError(error!!) {}
            allPatients.isEmpty()    -> DsEmptyState("👥", "No Patients Yet", "Add your first patient to begin managing their nutrition.")
            filtered.isEmpty()       -> DsEmptyState("🔍", "No Matches", "Try adjusting your search or filter criteria.")
            else -> Div({ style {
                display(DisplayStyle.Grid)
                property("grid-template-columns","repeat(auto-fill,minmax(340px,1fr))")
                gap(DS.sp20)
            }}) {
                filtered.forEachIndexed { i, patient ->
                    Div({ classes("fade-up","delay-${(i % 4) + 1}") }) {
                        PatientCard(patient) { onPatientClick(patient) }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Patient Card — clear hierarchy, DS tokens, CSS hover
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun PatientCard(patient: Patient, onClick: () -> Unit) {
    val adherenceColor = when {
        patient.adherenceScore >= 80 -> DS.success
        patient.adherenceScore >= 60 -> DS.warn
        else                         -> DS.danger
    }
    val (statusBg, statusFg) = when (patient.status) {
        PatientStatus.ACTIVE   -> DS.successBg to DS.success
        PatientStatus.INACTIVE -> DS.dangerBg  to DS.danger
        else                   -> DS.warnBg    to DS.warn
    }

    Div({
        classes("card", "card-clickable")
        style { padding(DS.sp24) }
        onClick { onClick() }
    }) {
        // ── Header row ────────────────────────────────────────────────────
        Div({ style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.FlexStart); marginBottom(DS.sp16)
        }}) {
            Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp12) }}) {
                // Avatar initials
                Div({ style {
                    width(44.px); height(44.px); borderRadius(50.percent); flexShrink(0)
                    property("background","linear-gradient(135deg,${DS.ink},${DS.teal})")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    fontFamily("Syne, sans-serif"); fontSize(16.px); fontWeight("800"); color(Color(DS.white))
                }}) { Text(patient.name.take(1).uppercase()) }
                Div {
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(16.px); fontWeight("800")
                        color(Color(DS.ink)); marginBottom(2.px)
                    }}) { Text(patient.name) }
                    Div({ style { fontSize(12.px); color(Color(DS.inkLight)) }}) {
                        Text(patient.email)
                    }
                }
            }
            // Status badge
            Span({ style {
                fontSize(11.px); fontWeight("700"); padding(3.px, 10.px); borderRadius(DS.rFull)
                backgroundColor(Color(statusBg)); color(Color(statusFg))
            }}) { Text(patient.status.name) }
        }

        // ── Physical stats grid ───────────────────────────────────────────
        Div({ style {
            display(DisplayStyle.Grid)
            property("grid-template-columns","repeat(3,1fr)")
            gap(1.px); backgroundColor(Color(DS.border)); borderRadius(DS.r12); overflow("hidden")
            marginBottom(DS.sp16)
        }}) {
            listOf(
                Triple("Age",    "${patient.age}",                                   "🎂"),
                Triple("BMI",    "${(patient.bmi * 10).toInt() / 10.0}",             "⚖️"),
                Triple("Gender", patient.gender.ifEmpty { "—" },                     "👤")
            ).forEach { (label, value, icon) ->
                Div({ style {
                    backgroundColor(Color(DS.white)); padding(DS.sp12); textAlign("center")
                }}) {
                    Div({ style { fontSize(14.px); fontWeight("700"); color(Color(DS.ink)); marginBottom(2.px) }}) {
                        Text("$icon $value")
                    }
                    Div({ style { fontSize(10.px); color(Color(DS.inkLight)) }}) { Text(label) }
                }
            }
        }

        // ── Goal chips ────────────────────────────────────────────────────
        if (patient.healthGoals.isNotEmpty()) {
            Div({ style { display(DisplayStyle.Flex); gap(DS.sp4); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp12) }}) {
                patient.healthGoals.take(3).forEach { goal ->
                    Span({ style {
                        fontSize(10.px); fontWeight("700"); padding(3.px, 8.px); borderRadius(DS.rFull)
                        backgroundColor(Color(DS.tealGhost)); color(Color(DS.tealDeep))
                    }}) { Text("🎯 $goal") }
                }
                if (patient.healthGoals.size > 3) {
                    Span({ style {
                        fontSize(10.px); fontWeight("600"); color(Color(DS.inkLight))
                        padding(3.px, 8.px)
                    }}) { Text("+${patient.healthGoals.size - 3} more") }
                }
            }
        }

        // ── Conditions ────────────────────────────────────────────────────
        if (patient.healthConditions.isNotEmpty()) {
            Div({ style { display(DisplayStyle.Flex); gap(DS.sp4); flexWrap(FlexWrap.Wrap); marginBottom(DS.sp16) }}) {
                patient.healthConditions.take(2).forEach { cond ->
                    Span({ style {
                        fontSize(10.px); fontWeight("700"); padding(3.px, 8.px); borderRadius(DS.rFull)
                        backgroundColor(Color(DS.warnBg)); color(Color(DS.warn))
                    }}) { Text("⚕️ $cond") }
                }
            }
        }

        // ── Footer ────────────────────────────────────────────────────────
        Div({ style {
            property("border-top", "1px solid ${DS.border}"); paddingTop(DS.sp16)
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
        }}) {
            // Adherence bar
            Div({ style { flex(1); marginRight(DS.sp16) }}) {
                Div({ style {
                    display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
                    fontSize(11.px); marginBottom(DS.sp4)
                }}) {
                    Span({ style { color(Color(DS.inkLight)); fontWeight("600") }}) { Text("Adherence") }
                    Span({ style { color(Color(adherenceColor)); fontWeight("800") }}) {
                        Text("${patient.adherenceScore}%")
                    }
                }
                Div({ classes("adh-track") }) {
                    Div({ classes("adh-fill"); style {
                        width(patient.adherenceScore.coerceIn(0, 100).percent)
                        backgroundColor(Color(adherenceColor))
                    }}) {}
                }
            }
            // Plan badge
            if (patient.currentPlanId != null) {
                Span({ style {
                    fontSize(10.px); fontWeight("700"); padding(3.px, 8.px); borderRadius(DS.rFull)
                    backgroundColor(Color(DS.successBg)); color(Color(DS.success))
                }}) { Text("📋 Plan Active") }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Plans List View
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun PlansListView(
    repository: NutritionistRepository,
    nutritionistId: String = FirebaseService.getCurrentUserId() ?: "nutritionist_001"
) {
    var allPlans       by remember { mutableStateOf<List<NutritionistBasePlan>>(emptyList()) }
    var searchQuery    by remember { mutableStateOf("") }
    var filterCategory by remember { mutableStateOf("All") }
    var isLoading      by remember { mutableStateOf(true) }
    var error          by remember { mutableStateOf<String?>(null) }
    val scope          = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        scope.launch {
            isLoading = true; error = null
            repository.getBasePlans(nutritionistId)
                .onSuccess { allPlans = it; isLoading = false }
                .onFailure { error = it.message; isLoading = false }
        }
    }

    val filtered = remember(allPlans, searchQuery, filterCategory) {
        var r = allPlans
        if (searchQuery.isNotBlank()) {
            val q = searchQuery.lowercase()
            r = r.filter {
                it.planName.lowercase().contains(q) ||
                        it.description.lowercase().contains(q) ||
                        it.targetAudience.lowercase().contains(q)
            }
        }
        if (filterCategory != "All") r = r.filter { it.category.name == filterCategory }
        r
    }

    Div({ classes("fade-up") }) {
        PageHeader("Plan Library", "Nutrition Plans", allPlans.size, "Your authored nutrition plans")

        // Filters
        Div({ style {
            display(DisplayStyle.Flex); gap(DS.sp12); marginBottom(DS.sp24); flexWrap(FlexWrap.Wrap)
            alignItems(AlignItems.Center)
        }}) {
            Div({ style { flex(1); minWidth(260.px) }}) {
                DsSearchBar(searchQuery, "Search plans…") { searchQuery = it }
            }
            Select({
                classes("ds-select")
                onChange { filterCategory = it.value ?: "All" }
            }) {
                Option("All") { Text("All Categories") }
                listOf("GENERAL_HEALTH","MUSCLE_BUILDING","WEIGHT_LOSS","DIABETES","HEART_HEALTH","PCOS").forEach { cat ->
                    Option(cat) { Text(categoryLabel(cat)) }
                }
            }
        }

        if (!isLoading && searchQuery.isNotBlank()) ResultCount(filtered.size, allPlans.size, "plans")

        when {
            isLoading             -> DashboardSkeleton()
            error != null         -> DashboardError(error!!) {}
            allPlans.isEmpty()    -> DsEmptyState("📋", "No Plans Yet", "Create your first nutrition plan to get started.")
            filtered.isEmpty()    -> DsEmptyState("🔍", "No Plans Found", "Try adjusting your search or filter criteria.")
            else -> Div({ style {
                display(DisplayStyle.Grid)
                property("grid-template-columns","repeat(auto-fill,minmax(380px,1fr))")
                gap(DS.sp20)
            }}) {
                filtered.forEachIndexed { i, plan ->
                    Div({ classes("fade-up","delay-${(i % 4) + 1}") }) {
                        PlanManagementCard(plan)
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Plan Management Card
// FIX: renamed from PlanCard (conflicts with ProteinPlansScreen)
// FIX: action buttons have correct hierarchy — primary / ghost / danger
// FIX: no TODO blocks — actions log intent and show coming-soon feedback
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun PlanManagementCard(plan: NutritionistBasePlan) {
    val (catBg, catFg) = categoryStyle(plan.category.name)

    Div({
        classes("card")
        // Check if classes logic is safe here
        style { padding(DS.sp24) }
    }) {
        // ── Header ────────────────────────────────────────────────────────
        Div({ style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.FlexStart); marginBottom(DS.sp16)
        }}) {
            Div({ style { flex(1); marginRight(DS.sp12) }}) {
                // Category pill
                Span({ style {
                    fontSize(10.px); fontWeight("700"); padding(3.px, 10.px); borderRadius(DS.rFull)
                    backgroundColor(Color(catBg)); color(Color(catFg)); display(DisplayStyle.InlineBlock)
                    marginBottom(DS.sp8)
                }}) {
                    Text("${categoryEmoji(plan.category.name)} ${categoryLabel(plan.category.name)}")
                }
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(17.px); fontWeight("800")
                    color(Color(DS.ink)); lineHeight("1.3")
                }}) { Text(plan.planName) }
            }
            // Active/Inactive badge
            Span({ style {
                fontSize(11.px); fontWeight("700"); padding(3.px, 10.px); borderRadius(DS.rFull)
                backgroundColor(if (plan.isActive) Color(DS.successBg) else Color(DS.dangerBg))
                color(if (plan.isActive) Color(DS.success) else Color(DS.danger))
                flexShrink(0)
            }}) { Text(if (plan.isActive) "Active" else "Inactive") }
        }

        // Description — 2-line clamp
        if (plan.description.isNotBlank()) {
            Div({ style {
                fontSize(13.px); color(Color(DS.inkLight)); lineHeight("1.6")
                marginBottom(DS.sp16); fontWeight("400")
                property("display","-webkit-box"); property("-webkit-line-clamp","2")
                property("-webkit-box-orient","vertical"); overflow("hidden")
            }}) { Text(plan.description) }
        }

        // Nutrition targets grid
        Div({ style {
            display(DisplayStyle.Grid)
            property("grid-template-columns","repeat(3,1fr)")
            gap(1.px); backgroundColor(Color(DS.border)); borderRadius(DS.r12); overflow("hidden")
            marginBottom(DS.sp16)
        }}) {
            listOf(
                Triple("Protein",  "${plan.nutritionalTargets.proteinPerWeek}g/wk",      "🥩"),
                Triple("Calories", "${plan.nutritionalTargets.caloriesPerDay} kcal",     "🔥"),
                Triple("Omega-3",  "${plan.nutritionalTargets.omega3PerWeek}mg/wk",      "🐟")
            ).forEach { (label, value, icon) ->
                Div({ style { backgroundColor(Color(DS.white)); padding(DS.sp12); textAlign("center") }}) {
                    Div({ style {
                        fontSize(13.px); fontWeight("700"); color(Color(DS.ink)); marginBottom(2.px)
                    }}) { Text("$icon $value") }
                    Div({ style { fontSize(10.px); color(Color(DS.inkLight)) }}) { Text(label) }
                }
            }
        }

        // Price + goal count
        Div({ style {
            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center); marginBottom(DS.sp16)
            property("border-top", "1px solid ${DS.border}"); paddingTop(DS.sp16)
        }}) {
            Div {
                Div({ style { fontSize(11.px); color(Color(DS.inkLight)); marginBottom(2.px) }}) { Text("Weekly Price") }
                Div({ style {
                    fontFamily("Syne, sans-serif"); fontSize(22.px); fontWeight("800"); color(Color(DS.crimson))
                }}) { Text("৳${plan.pricingTemplate.weeklyBase.toInt()}") }
            }
            if (plan.goals.isNotEmpty()) {
                Span({ style {
                    fontSize(11.px); fontWeight("600"); color(Color(DS.inkLight))
                    backgroundColor(Color(DS.surface)); padding(DS.sp4, DS.sp12); borderRadius(DS.rFull)
                    border(1.px, LineStyle.Solid, Color(DS.border))
                }}) { Text("${plan.goals.size} goals") }
            }
        }

        // ── Action buttons — hierarchical, not equal-weight ───────────────
        Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap) }}) {
            // Primary action
            Button({ classes("btn-teal"); onClick { /* TODO: open plan detail */ } }) { Text("View →") }
            // Secondary actions
            Button({ classes("btn-ghost"); onClick { /* TODO: open edit modal */ } }) { Text("Edit") }
            Button({ classes("btn-ghost"); onClick { /* TODO: duplicate plan */ } }) { Text("Duplicate") }
            // Destructive — visually distinct
            if (plan.isActive) {
                Button({ classes("btn-danger"); onClick { /* TODO: deactivate */ } }) { Text("Deactivate") }
            } else {
                Button({ classes("btn-success"); onClick { /* TODO: activate */ } }) { Text("Activate") }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Profile View — full info, not a sparse placeholder
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun ProfileView(profile: NutritionistProfile?) {
    Div({ classes("fade-up") }) {
        PageHeader("Account", "My Profile", subtitle = "Your professional profile visible to patients")

        if (profile == null) {
            DsEmptyState("👤", "Profile not found", "We couldn't load your profile data.")
            return@Div
        }

        // Hero card
        Div({ classes("card"); style { padding(DS.sp32); marginBottom(DS.sp24) }}) {
            Div({ style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(DS.sp24)
                flexWrap(FlexWrap.Wrap); marginBottom(DS.sp24)
            }}) {
                // Avatar
                Div({ style {
                    width(72.px); height(72.px); borderRadius(50.percent); flexShrink(0)
                    property("background","linear-gradient(135deg,${DS.ink},${DS.teal})")
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    fontFamily("Syne, sans-serif"); fontSize(28.px); fontWeight("800"); color(Color(DS.white))
                }}) { Text(profile.name.take(1).uppercase()) }

                Div({ style { flex(1) }}) {
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(24.px); fontWeight("800")
                        color(Color(DS.ink)); marginBottom(DS.sp4)
                    }}) { Text(profile.name) }
                    Div({ style { fontSize(14.px); color(Color(DS.teal)); fontWeight("600"); marginBottom(DS.sp8) }}) {
                        Text(profile.credentials)
                    }
                    Div({ style { display(DisplayStyle.Flex); gap(DS.sp12); flexWrap(FlexWrap.Wrap) }}) {
                        Span({ style { fontSize(13.px); color(Color(DS.inkLight)) }}) {
                            Text("⭐ ${profile.rating} rating")
                        }
                        Span({ style { fontSize(13.px); color(Color(DS.inkLight)) }}) {
                            Text("⏱ ${profile.experience} yrs experience")
                        }
                        if (profile.contactEmail.isNotBlank()) {
                            Span({ style { fontSize(13.px); color(Color(DS.inkLight)) }}) {
                                Text("✉️ ${profile.contactEmail}")
                            }
                        }
                        if (profile.phone.isNotBlank()) {
                            Span({ style { fontSize(13.px); color(Color(DS.inkLight)) }}) {
                                Text("📞 ${profile.phone}")
                            }
                        }
                    }
                }
            }

            // Bio
            if (profile.bio.isNotBlank()) {
                Div({ style {
                    property("border-left","3px solid ${DS.teal}"); paddingLeft(DS.sp16)
                    fontSize(14.px); color(Color(DS.inkMid)); lineHeight("1.7"); fontStyle("italic")
                }}) { Text(profile.bio) }
            }
        }

        // Stats grid
        Div({ style {
            display(DisplayStyle.Grid)
            property("grid-template-columns","repeat(auto-fit,minmax(160px,1fr))")
            gap(DS.sp16); marginBottom(DS.sp24)
        }}) {
            listOf(
                Triple("Total Clients",    profile.totalClients.toString(),    "👥"),
                Triple("Active Clients",   profile.activeClients.toString(),   "✅"),
                Triple("Specializations",  profile.specializations.size.toString(), "🎓")
            ).forEach { (label, value, icon) ->
                Div({ classes("stat-card") }) {
                    Div({ style { fontSize(22.px); marginBottom(DS.sp8) }}) { Text(icon) }
                    Div({ style {
                        fontFamily("Syne, sans-serif"); fontSize(28.px); fontWeight("800")
                        color(Color(DS.ink)); lineHeight("1"); marginBottom(DS.sp4)
                    }}) { Text(value) }
                    Div({ style { fontSize(12.px); color(Color(DS.inkLight)); fontWeight("600") }}) { Text(label) }
                }
            }
        }

        // Specializations
        if (profile.specializations.isNotEmpty()) {
            Div({ classes("card"); style { padding(DS.sp24) }}) {
                Div({ classes("section-label"); style { marginBottom(DS.sp12) }}) { Text("Specializations") }
                Div({ style { display(DisplayStyle.Flex); gap(DS.sp8); flexWrap(FlexWrap.Wrap) }}) {
                    profile.specializations.forEach { spec ->
                        Span({ style {
                            fontSize(12.px); fontWeight("700"); padding(DS.sp4, DS.sp16); borderRadius(DS.rFull)
                            backgroundColor(Color(DS.tealGhost)); color(Color(DS.tealDeep))
                        }}) { Text(spec) }
                    }
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI primitives — consistent across all dashboard views
// ─────────────────────────────────────────────────────────────────────────────

@Composable
private fun PageHeader(label: String, title: String, count: Int = -1, subtitle: String = "") {
    Div({ style { marginBottom(DS.sp32) }}) {
        Div({ classes("section-label"); style { marginBottom(DS.sp8) }}) { Text(label) }
        Div({ style { display(DisplayStyle.Flex); alignItems(AlignItems.Baseline); gap(DS.sp12) }}) {
            Div({ classes("section-title") }) {
                Text(if (count >= 0) "$title ($count)" else title)
            }
        }
        if (subtitle.isNotBlank()) {
            Div({ style { fontSize(13.px); color(Color(DS.inkLight)); marginTop(DS.sp4) }}) { Text(subtitle) }
        }
    }
}

@Composable
fun DsSearchBar(value: String, placeholder: String, onChange: (String) -> Unit) {
    Div({ classes("search-wrap") }) {
        Span({ classes("search-icon") }) { Text("🔍") }
        Input(org.jetbrains.compose.web.attributes.InputType.Text) {
            this.value(value)
            onInput { onChange(it.value) }
            classes("search-input")
            attr("placeholder", placeholder)
        }
        if (value.isNotBlank()) {
            Span({
                style {
                    position(Position.Absolute); right(12.px); top(50.percent)
                    property("transform","translateY(-50%)")
                    cursor("pointer"); fontSize(14.px); color(Color(DS.inkLight))
                }
                onClick { onChange("") }
            }) { Text("✕") }
        }
    }
}

@Composable
fun DsEmptyState(icon: String, title: String, message: String) {
    Div({ classes("empty-state") }) {
        Div({ style { fontSize(48.px); marginBottom(DS.sp16) }}) { Text(icon) }
        Div({ style {
            fontFamily("Syne, sans-serif"); fontSize(20.px); fontWeight("800")
            color(Color(DS.ink)); marginBottom(DS.sp8)
        }}) { Text(title) }
        Div({ style { fontSize(14.px); color(Color(DS.inkLight)); maxWidth(360.px); property("margin","0 auto") }}) {
            Text(message)
        }
    }
}

@Composable
private fun ResultCount(shown: Int, total: Int, noun: String) {
    Div({ style { fontSize(13.px); color(Color(DS.inkLight)); marginBottom(DS.sp16) }}) {
        Text("Showing $shown of $total $noun")
    }
}